export interface NetworkIdentity {
  ip: string;
  location: string;
  isp: string;
  source: "live" | "cache" | "ip-only" | "unavailable";
}

const CACHE_KEY = "kirkwat-network-identity-v1";
const CACHE_TTL = 30 * 60 * 1000;
let pendingIdentity: Promise<NetworkIdentity> | null = null;
let memoryIdentity: { value: NetworkIdentity; savedAt: number } | null = null;

function readCachedIdentity(): NetworkIdentity | null {
  if (
    memoryIdentity &&
    Date.now() - memoryIdentity.savedAt <= CACHE_TTL
  ) {
    return { ...memoryIdentity.value, source: "cache" };
  }
  try {
    const saved =
      typeof window !== "undefined" &&
      typeof window.localStorage?.getItem === "function"
        ? JSON.parse(window.localStorage.getItem(CACHE_KEY) || "null")
        : null;
    if (!saved?.value || Date.now() - Number(saved.savedAt || 0) > CACHE_TTL) {
      return null;
    }
    return { ...saved.value, source: "cache" };
  } catch {
    return null;
  }
}

function saveIdentity(value: NetworkIdentity) {
  memoryIdentity = { value, savedAt: Date.now() };
  try {
    if (
      typeof window !== "undefined" &&
      typeof window.localStorage?.setItem === "function"
    ) {
      window.localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ value, savedAt: Date.now() }),
      );
    }
  } catch {}
}

async function fetchJson(url: string, timeoutMs = 5000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`network identity ${response.status}`);
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

async function resolveIdentity(): Promise<NetworkIdentity> {
  try {
    const data = await fetchJson("https://ipwho.is/");
    if (data?.success !== false && data?.ip) {
      const location = [data.country, data.region, data.city]
        .filter(Boolean)
        .join(" · ");
      const value: NetworkIdentity = {
        ip: String(data.ip),
        location: location || "归属地未知",
        isp: String(data.connection?.isp || data.connection?.org || ""),
        source: "live",
      };
      saveIdentity(value);
      return value;
    }
  } catch {}

  try {
    const data = await fetchJson("https://api64.ipify.org?format=json", 4000);
    if (data?.ip) {
      const value: NetworkIdentity = {
        ip: String(data.ip),
        location: "归属地服务暂不可用",
        isp: "",
        source: "ip-only",
      };
      saveIdentity(value);
      return value;
    }
  } catch {}

  return (
    readCachedIdentity() || {
      ip: "获取失败",
      location: "网络不可用",
      isp: "",
      source: "unavailable",
    }
  );
}

export function getNetworkIdentity(): Promise<NetworkIdentity> {
  const cached = readCachedIdentity();
  if (cached) return Promise.resolve(cached);
  if (!pendingIdentity) {
    pendingIdentity = resolveIdentity().finally(() => {
      pendingIdentity = null;
    });
  }
  return pendingIdentity;
}
